import type { ProcessFileType } from "@quickpoint.me/shared";

/**
 * Excel files are converted to CSV at upload time so the viewer only ever
 * parses text (keeps the viewer bundle free of spreadsheet libraries).
 * Only the first worksheet is kept — that is what charts from.
 *
 * Note: browsers report CSV files as "application/vnd.ms-excel" when Excel
 * is installed, so that mime type is only treated as a spreadsheet when the
 * file extension is not .csv.
 */

const EXCEL_MIMES = [
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheet.sheet",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

export function isExcelUpload(file: ProcessFileType): boolean {
	if (!EXCEL_MIMES.includes(file.fileType)) return false;
	return !file.fileName.toLowerCase().endsWith(".csv");
}

/** A .csv file mislabelled by the browser as vnd.ms-excel. */
export function isMislabelledCsv(file: ProcessFileType): boolean {
	return (
		EXCEL_MIMES.includes(file.fileType) &&
		file.fileName.toLowerCase().endsWith(".csv")
	);
}

function csvEscape(value: string): string {
	if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
	return value;
}

// exceljs cell values can be primitives, dates, formulas, or rich text
function cellToString(value: unknown): string {
	if (value === null || value === undefined) return "";
	if (value instanceof Date) return value.toISOString().slice(0, 10);
	if (typeof value === "object") {
		const v = value as {
			result?: unknown;
			richText?: { text: string }[];
			text?: string;
			hyperlink?: string;
		};
		if (v.richText) return v.richText.map((r) => r.text).join("");
		if (v.result !== undefined) return cellToString(v.result);
		if (v.text !== undefined) return v.text;
		return "";
	}
	return String(value);
}

/**
 * Parse the first worksheet of an .xlsx file into CSV text.
 * Throws if the file cannot be read (e.g. legacy binary .xls).
 */
function blobToArrayBuffer(data: Blob): Promise<ArrayBuffer> {
	// Blob.arrayBuffer() is missing in jsdom — fall back to FileReader
	if (typeof data.arrayBuffer === "function") return data.arrayBuffer();
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as ArrayBuffer);
		reader.onerror = () => reject(reader.error);
		reader.readAsArrayBuffer(data);
	});
}

export async function excelToCsv(data: Blob): Promise<string> {
	const { Workbook } = await import("exceljs");
	const workbook = new Workbook();
	await workbook.xlsx.load(await blobToArrayBuffer(data));
	const sheet = workbook.worksheets[0];
	if (!sheet) throw new Error("Workbook has no worksheets");

	const lines: string[] = [];
	sheet.eachRow({ includeEmpty: false }, (row) => {
		// row.values is 1-indexed; slot 0 is always empty
		const values = row.values as unknown[];
		const cells: string[] = [];
		for (let c = 1; c < Math.max(values.length, sheet.columnCount + 1); c++) {
			cells.push(csvEscape(cellToString(values[c])));
		}
		lines.push(cells.join(","));
	});
	if (lines.length === 0) throw new Error("Worksheet is empty");
	return lines.join("\n");
}

/** Returns a copy of the file with CSV data, name, and mime type. */
export async function convertExcelUpload(
	file: ProcessFileType,
): Promise<ProcessFileType> {
	const csv = await excelToCsv(file.data);
	const fileName = `${file.fileName.replace(/\.(xlsx|xls)$/i, "")}.csv`;
	const csvFile = new File([csv], fileName, { type: "text/csv" });
	return {
		...file,
		data: csvFile,
		fileName,
		fileSize: csvFile.size,
		fileType: "text/csv",
	};
}
